import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { toLightweightDeviation } from '../utils/toLightweightDeviation';
import Component from './components/GalleryNodeView';
import { deleteExtension, getExternalRenderer } from '../../components/utils/tiptap';
import { EXTENSION_TYPE_NAME } from './constants';
import omitBy from 'lodash/omitBy';
export var MenuState = /*#__PURE__*/ function(MenuState) {
    MenuState[MenuState["HIDDEN"] = 0] = "HIDDEN";
    MenuState[MenuState["SHOW"] = 1] = "SHOW";
    return MenuState;
}({});
export const DaGallery = Node.create({
    name: 'da-gallery',
    group: 'block unlinkable',
    inline: false,
    draggable: true,
    addStorage () {
        return {
            menuState: 0
        };
    },
    addOptions () {
        return {
            // HTMLAttributes: {},
            defaultWidth: 780,
            formMaxWidth: 780,
            smallMedia: false,
            canLink: true,
            canConfigure: true,
            canResize: true
        };
    },
    addAttributes () {
        var _this_options_defaultAlignment;
        return {
            deviations: {
                default: null
            },
            width: {
                default: null
            },
            alignment: {
                default: (_this_options_defaultAlignment = this.options.defaultAlignment) !== null && _this_options_defaultAlignment !== void 0 ? _this_options_defaultAlignment : 'center'
            },
            layout: {
                default: 'grid'
            },
            layoutSpacing: {
                default: 8
            },
            layoutRowAlignment: {
                default: 'left'
            },
            itemCropping: {
                default: 'crop'
            },
            gridItemWidth: {
                default: null
            },
            gridItemHeight: {
                default: null
            },
            torpedoRowHeight: {
                default: null
            },
            linkOverrides: {
                default: {}
            }
        };
    },
    /**
   * Used to parse incoming html, usually via paste
   */ parseHTML () {
        return [
            {
                tag: `div[data-da-type="${EXTENSION_TYPE_NAME}"]`,
                getAttrs (node) {
                    if (!node || !node.getAttribute) {
                        return false;
                    }
                    const dataType = node.getAttribute('data-da-type');
                    if (!dataType || dataType !== EXTENSION_TYPE_NAME) {
                        return false;
                    }
                    try {
                        let deviations = [];
                        let linkOverrides = {};
                        if (node.getAttribute('data-deviations') !== null) {
                            var _node_getAttribute;
                            deviations = JSON.parse((_node_getAttribute = node.getAttribute('data-deviations')) !== null && _node_getAttribute !== void 0 ? _node_getAttribute : '[]');
                            var _node_getAttribute1;
                            linkOverrides = JSON.parse((_node_getAttribute1 = node.getAttribute('data-link-overrides')) !== null && _node_getAttribute1 !== void 0 ? _node_getAttribute1 : '{}');
                        } else {
                            const innerDeviations = node.querySelectorAll('div[data-deviation]');
                            innerDeviations.forEach((iNode)=>{
                                deviations.push(JSON.parse(`${iNode.getAttribute('data-deviation')}`));
                            });
                            const links = node.querySelectorAll('a[data-deviationId]');
                            links.forEach((link)=>{
                                var _link_getAttribute;
                                linkOverrides[(_link_getAttribute = link.getAttribute('data-deviationId')) !== null && _link_getAttribute !== void 0 ? _link_getAttribute : ''] = link.getAttribute('href');
                            });
                        }
                        var _node_getAttribute2, _node_getAttribute3, _node_getAttribute4, _node_getAttribute5, _node_getAttribute6, _node_getAttribute7, _node_getAttribute8, _node_getAttribute9;
                        const attrs = {
                            width: maybeNumber((_node_getAttribute2 = node.getAttribute('data-width')) !== null && _node_getAttribute2 !== void 0 ? _node_getAttribute2 : undefined),
                            deviations,
                            linkOverrides,
                            layout: (_node_getAttribute3 = node.getAttribute('data-layout')) !== null && _node_getAttribute3 !== void 0 ? _node_getAttribute3 : undefined,
                            layoutSpacing: maybeNumber((_node_getAttribute4 = node.getAttribute('data-layoutspacing')) !== null && _node_getAttribute4 !== void 0 ? _node_getAttribute4 : undefined),
                            layoutRowAlignment: (_node_getAttribute5 = node.getAttribute('data-layoutrowalignment')) !== null && _node_getAttribute5 !== void 0 ? _node_getAttribute5 : undefined,
                            itemCropping: (_node_getAttribute6 = node.getAttribute('data-itemcropping')) !== null && _node_getAttribute6 !== void 0 ? _node_getAttribute6 : undefined,
                            gridItemWidth: maybeNumber((_node_getAttribute7 = node.getAttribute('data-griditemwidth')) !== null && _node_getAttribute7 !== void 0 ? _node_getAttribute7 : undefined),
                            gridItemHeight: maybeNumber((_node_getAttribute8 = node.getAttribute('data-griditemheight')) !== null && _node_getAttribute8 !== void 0 ? _node_getAttribute8 : undefined),
                            torpedoRowHeight: maybeNumber((_node_getAttribute9 = node.getAttribute('data-torpedorowheight')) !== null && _node_getAttribute9 !== void 0 ? _node_getAttribute9 : undefined)
                        };
                        return attrs;
                    } catch (e) {}
                    return null;
                }
            }
        ];
    },
    /**
   * Used to render HTML version of the doc, this is not used for viewer and not used
   * in edit mode either.
   *
   * Will be used for public API on the server
   *
   * Output somehting like:
   *
   * <div class="gallery" data-type="grid">
   *   <img />
   *   <img />
   *   ...
   * </div>
   *
   */ renderHTML ({ HTMLAttributes }) {
        try {
            const { deviations, linkOverrides } = HTMLAttributes;
            const deviationMediaService = getExternalRenderer(this.editor, 'mediaService');
            if (!deviationMediaService) {
                return [
                    'div'
                ];
            }
            if (deviations.length === 0) {
                return [
                    'span'
                ];
            }
            const imgs = deviations.map((deviation)=>{
                const props = {};
                try {
                    const dev = deviationMediaService.getSrcSetForDeviationPreview(deviation, true, 780);
                    props.src = dev.src;
                    props.srcSet = dev.srcSet;
                } catch (e) {
                    const dev = deviationMediaService.getSmallestThumbForArtStage(deviation, 250, 250);
                    props.src = dev === null || dev === void 0 ? void 0 : dev.src;
                }
                return [
                    'a',
                    {
                        href: (linkOverrides === null || linkOverrides === void 0 ? void 0 : linkOverrides[deviation.deviationId]) ? linkOverrides[deviation.deviationId] : deviation.url
                    },
                    [
                        'img',
                        mergeAttributes(_object_spread_props(_object_spread({
                            alt: deviation.title
                        }, props), {
                            'data-deviation-item': JSON.stringify(deviation)
                        }))
                    ]
                ];
            });
            const attribs = {};
            for (const key of Object.keys(HTMLAttributes)){
                if (key === 'deviations' || key === 'linkOverrides') {
                    continue;
                }
                attribs[`data-${key}`] = HTMLAttributes[key];
            }
            return [
                'div',
                mergeAttributes(_object_spread({
                    'data-da-type': 'da-gallery',
                    'data-deviations': JSON.stringify(deviations),
                    'data-linkoverrides': JSON.stringify(linkOverrides)
                }, attribs)),
                ...imgs
            ];
        } catch (e) {
            return [
                'span'
            ];
        }
    },
    addNodeView () {
        return ReactNodeViewRenderer(Component);
    },
    onSelectionUpdate () {
        if (this.editor.isActive('da-gallery')) {
            this.editor.commands.daGalleryOpenSettingsMenu();
        } else {
            this.editor.commands.daGalleryCloseSettingsMenu();
        }
    },
    addCommands () {
        return {
            daGalleryAddDeviations: (deviations, isEdit)=>({ commands, dispatch })=>{
                    if (dispatch) {
                        this.storage.menuState = 1;
                        if (isEdit) {
                            return commands.updateAttributes(this.name, {
                                deviations
                            });
                        }
                        return commands.insertContent({
                            type: this.name,
                            attrs: {
                                deviations: deviations.map(toLightweightDeviation)
                            }
                        });
                    }
                    return true;
                },
            daGalleryOpenSettingsMenu: ()=>({ editor, chain, dispatch })=>{
                    if (dispatch) {
                        this.storage.menuState = 1;
                    }
                    return true;
                },
            daGalleryCloseSettingsMenu: ()=>({ editor, chain, dispatch })=>{
                    if (dispatch) {
                        this.storage.menuState = 0;
                    }
                    return true;
                },
            daGalleryDelete: ()=>({ editor, chain, dispatch, commands, state })=>{
                    if (deleteExtension(EXTENSION_TYPE_NAME, editor, dispatch, state)) {
                        this.storage.menuState = 0;
                        return true;
                    }
                    return true;
                },
            daGalleryAlign: (alignment)=>({ editor, chain, dispatch, commands })=>{
                    if (!this.options.canAlign) {
                        return false;
                    }
                    if (dispatch) {
                        return commands.updateAttributes(this.name, {
                            alignment
                        });
                    }
                    return true;
                },
            daGallerySetWidth: (width)=>({ editor, chain, dispatch, commands })=>{
                    if (dispatch) {
                        return commands.updateAttributes(this.name, {
                            width: +width
                        });
                    }
                    return true;
                },
            daGallerySetGridItemWidth: (width)=>({ editor, chain, dispatch, commands })=>{
                    if (dispatch) {
                        return commands.updateAttributes(this.name, {
                            gridItemWidth: +width
                        });
                    }
                    return true;
                },
            daGallerySetGridItemHeight: (height)=>({ editor, chain, dispatch, commands })=>{
                    if (dispatch) {
                        return commands.updateAttributes(this.name, {
                            gridItemHeight: +height
                        });
                    }
                    return true;
                },
            daGallerySetLayoutSpacing: (value)=>({ editor, chain, dispatch, commands })=>{
                    if (dispatch) {
                        return commands.updateAttributes(this.name, {
                            layoutSpacing: +value
                        });
                    }
                    return true;
                },
            daGallerySetTorpedoRowHeight: (value)=>({ editor, chain, dispatch, commands })=>{
                    if (dispatch) {
                        return commands.updateAttributes(this.name, {
                            torpedoRowHeight: +value
                        });
                    }
                    return true;
                },
            daGallerySetItemCropping: (value)=>({ editor, chain, dispatch, commands })=>{
                    if (dispatch) {
                        return commands.updateAttributes(this.name, {
                            itemCropping: value
                        });
                    }
                    return true;
                },
            daGallerySetRowAlignment: (value)=>({ editor, chain, dispatch, commands })=>{
                    if (dispatch) {
                        return commands.updateAttributes(this.name, {
                            layoutRowAlignment: value
                        });
                    }
                    return true;
                },
            daGallerySetLinkOverrides: (value)=>({ editor, chain, dispatch, commands })=>{
                    if (dispatch) {
                        return commands.updateAttributes(this.name, {
                            linkOverrides: omitBy(value, (v)=>{
                                return v === '' || !v;
                            })
                        });
                    }
                    return true;
                },
            daGalleryChangeLayout: (layout)=>({ editor, chain, dispatch, commands })=>{
                    if (dispatch) {
                        return commands.updateAttributes(this.name, {
                            layout
                        });
                    }
                    return true;
                }
        };
    }
});
function maybeNumber(value) {
    if (value === undefined) {
        return value;
    }
    return +value;
}

//# sourceMappingURL=gallery.js.map